// pages/master/vtds/MasterVtdsPage.jsx
'use client';

import { useState, useEffect, useMemo } from 'react';
import DataTable from '../../../components/DataTable';
import ActionMenu from '../../../components/ActionMenu';
import { useNotifier } from '../../../components/Notifier';
import DeleteConfirmDialog from '../../../components/DeleteConfirmDialog';

import { useVtdsList, deleteVtds } from '../../../stores/vtdsStore';
import { fleetApi } from '../../../api/axiosClient';
import { useCompanyList } from '../../../stores/companyStore';

import MasterVtdsAddEdit from './MasterVtdsAddEdit';
import { useLanguage } from '../../../utils/i18n';
const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;

function MasterVtdsPageContent() {
  const currentUserRole = typeof window !== 'undefined' ? localStorage.getItem('rolename') || localStorage.getItem('role_name') || '' : '';

  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const notify = useNotifier();
  const { t } = useLanguage();

  const { companyOptions } = useCompanyList();
  const [selectedCompanies, setSelectedCompanies] = useState(() => {
    try {
      const stored = typeof window !== 'undefined' ? localStorage.getItem('companyId') : null;
      return stored ? [{ value: String(stored), label: String(stored) }] : [];
    } catch {
      return [];
    }
  });

  const companyOptionsNormalized = useMemo(
    () =>
      (companyOptions || []).map((c) => ({
        value: String(c.value ?? c.id ?? c),
        label: c.label ?? c.name ?? String(c.value ?? c.id ?? c)
      })),
    [companyOptions]
  );

  useEffect(() => {
    try {
      if (!companyOptionsNormalized || companyOptionsNormalized.length === 0) return;
      const raw = typeof window !== 'undefined' ? localStorage.getItem('user') : null;
      if (!raw) return;

      let u = null;
      try {
        u = JSON.parse(raw);
      } catch {
        return;
      }

      const companyId = u?.company ?? u?.company_id ?? null;
      if (companyId == null) return;

      const companyIdStr = String(companyId);
      const match = companyOptionsNormalized.find((o) => String(o.value) === companyIdStr);
      const selected = match
        ? [{ value: String(match.value), label: match.label ?? String(match.value) }]
        : [{ value: companyIdStr, label: companyIdStr }];

      setSelectedCompanies((prev) => {
        const prevVal = prev && prev[0] && String(prev[0].value);
        if (prevVal === selected[0].value) return prev;
        return selected;
      });
    } catch (e) {
      console.error('Auto select company from user failed', e);
    }
  }, [companyOptionsNormalized]);

  const filterCompanyIds = useMemo(() => {
    if (!selectedCompanies || selectedCompanies.length === 0) return '';
    const hasAll = selectedCompanies.some((s) => String(s.value) === 'all');
    if (hasAll) return '';
    return selectedCompanies.map((s) => String(s.value)).join(',');
  }, [selectedCompanies]);

  const [searchField, setSearchField] = useState('license_plate');
  const [searchRaw, setSearchRaw] = useState('');
  const [searchValue, setSearchValue] = useState('');

  const searchFieldOptions = useMemo(
    () => [
      { value: 'all', label: t('general.searchAll', 'All') },
      { value: 'license_plate', label: t('general.licensePlate', 'License Plate') },
      { value: 'gps_vendor', label: t('masterVtds.gpsVendor', 'GPS Vendor') },
      { value: 'gps_type', label: t('masterVtds.gpsType', 'GPS Type') },
      { value: 'port', label: t('masterVtds.port', 'Port') },
      { value: 'imei_no', label: t('masterVtds.imeiNo', 'IMEI No') },
      { value: 'no_gsm', label: t('masterVtds.simcardNo', 'Simcard No') },
      { value: 'purchase_date', label: t('masterVtds.purchaseDate', 'Purchase Date') },
      { value: 'description', label: t('general.description', 'Description') }
    ],
    [t]
  );

  const [localVtds, setLocalVtds] = useState([]);
  const [localLoading, setLocalLoading] = useState(false);
  const [localTotal, setLocalTotal] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearchValue(String(searchRaw ?? '').trim());
      setPaginationModel((p) => ({ ...p, page: 0 }));
    }, 700);

    return () => clearTimeout(t);
  }, [searchRaw, searchField]);

  const hookSearchField = searchField === 'all' ? '' : searchField;
  const hookSearchValue = searchField === 'all' ? '' : searchRaw;

  const {
    data: hookRows,
    totalRecord: hookTotal,
    isLoading: hookLoading
  } = useVtdsList(paginationModel.page, paginationModel.pageSize, hookSearchField, hookSearchValue, filterCompanyIds);

  const rows = searchField === 'all' ? localVtds : hookRows || [];
  const isLoading = searchField === 'all' ? localLoading : hookLoading;
  const totalRecord = searchField === 'all' ? localTotal : hookTotal || 0;

  useEffect(() => {
    if (searchField !== 'all') return;
    const controller = new AbortController();
    const fetchAllSearch = async () => {
      setLocalLoading(true);
      try {
        const start = paginationModel.page * paginationModel.pageSize;
        const length = paginationModel.pageSize;

        const columns = [];
        if (selectedCompanies && selectedCompanies.length > 0) {
          const hasAll = selectedCompanies.some((s) => String(s.value) === 'all');
          if (!hasAll) {
            const values = selectedCompanies.map((s) => String(s.value));
            if (values.length === 1) {
              columns.push({ name: 'company_id', logic_operator: '=', operator: 'AND', value: values[0] });
            } else {
              columns.push({ name: 'company_id', logic_operator: 'in', operator: 'AND', value: values.join(',') });
            }
          }
        }

        if (columns.length === 0) {
          // Default minimal object untuk menghindari error backend jika columns kosong
          columns.push({ logic_operator: 'like', name: 'gps_vendor', operator: 'AND', value: '' });
        }

        let filters = [];
        if (searchValue) {
          filters = [
            { name: 'license_plate', table_name: 'm_vehicle', value: searchValue },
            { name: 'name', table_name: 'm_company', value: searchValue },
            { name: 'gps_vendor', value: searchValue },
            { name: 'gps_type', value: searchValue },
            { name: 'port', value: searchValue },
            { name: 'imei_no', value: searchValue },
            { name: 'no_gsm', value: searchValue },
            { name: 'purchase_date', value: searchValue },
            { name: 'description', value: searchValue }
          ];
        }

        const payload = {
          paging: { start, length },
          orders: { columns: ['id'], ascending: false },
          joins: [
            { name: 'company', column_results: ['name'] },
            { name: 'vehicle', column_results: ['license_plate'] }
          ],
          columns: columns,
          filters: filters
        };
        const resp = await fleetApi.post('/vtds/search' + getTzParam(), payload, { signal: controller.signal });
        const data = resp?.data ?? null;
        const resultRows = Array.isArray(data) ? data : (data?.data ?? []);
        const total = Array.isArray(data) ? resultRows.length : (data?.totalRecord ?? data?.totalRecords ?? data?.dataSize ?? 0);

        setLocalVtds(resultRows);
        setLocalTotal(total);
      } catch (err) {
        if (err.name !== 'AbortError') console.error('vtds search all error', err);
      } finally {
        if (!controller.signal.aborted) setLocalLoading(false);
      }
    };

    fetchAllSearch();
    return () => controller.abort();
  }, [searchField, searchValue, paginationModel, selectedCompanies]);

  const handleFetchAllForExport = async () => {
    try {
      const length = totalRecord > 0 ? totalRecord : 9999;
      const columns = [];
      if (selectedCompanies && selectedCompanies.length > 0) {
        const hasAll = selectedCompanies.some((s) => String(s.value) === 'all');
        if (!hasAll) {
          const values = selectedCompanies.map((s) => String(s.value));
          if (values.length === 1) {
            columns.push({ name: 'company_id', logic_operator: '=', operator: 'AND', value: values[0] });
          } else {
            columns.push({ name: 'company_id', logic_operator: 'in', operator: 'AND', value: values.join(',') });
          }
        }
      }

      if (columns.length === 0) {
        columns.push({ logic_operator: 'like', name: 'gps_vendor', operator: 'AND', value: '' });
      }

      let filters = [];
      if (searchValue) {
        if (searchField === 'all') {
          filters = [
            { name: 'license_plate', table_name: 'm_vehicle', value: searchValue },
            { name: 'name', table_name: 'm_company', value: searchValue },
            { name: 'gps_vendor', value: searchValue },
            { name: 'gps_type', value: searchValue },
            { name: 'port', value: searchValue },
            { name: 'imei_no', value: searchValue },
            { name: 'no_gsm', value: searchValue },
            { name: 'purchase_date', value: searchValue },
            { name: 'description', value: searchValue }
          ];
        } else {
          const opt = searchFieldOptions.find((o) => o.value === searchField);
          if (opt) {
            const searchCol = {
              name: opt.fieldName || searchField,
              logic_operator: 'like',
              operator: 'AND',
              value: searchValue
            };
            if (opt.tableName) searchCol.table_name = opt.tableName;
            columns.push(searchCol);
          }
        }
      }

      const payload = {
        paging: { start: 0, length },
        orders: { columns: ['id'], ascending: false },
        joins: [
          { name: 'company', column_results: ['name'] },
          { name: 'vehicle', column_results: ['license_plate'] }
        ],
        columns: columns,
        filters: filters
      };

      const resp = await fleetApi.post('/vtds/search' + getTzParam(), payload);
      const data = resp?.data ?? null;
      const resultRows = Array.isArray(data) ? data : (data?.data ?? []);
      return resultRows;
    } catch (err) {
      console.error('vtds export all error', err);
      return [];
    }
  };

  const handlePageChange = (model) => {
    setPaginationModel((prev) => {
      if (prev.page === model.page && prev.pageSize === model.pageSize) return prev;
      return model;
    });
  };

  const handleCompanyChange = (options = []) => {
    const sanitized = (options || []).map((o) => ({ value: String(o.value), label: o.label ?? String(o.value) }));
    setSelectedCompanies(sanitized);
    try {
      if (typeof window !== 'undefined') {
        if (sanitized.length === 1) localStorage.setItem('companyId', String(sanitized[0].value));
        else localStorage.removeItem('companyId');
      }
    } catch {
      console.warn('Gagal menyimpan companyId ke localStorage');
    }
  };

  const handleAddClick = () => {
    setEditingId(null);
    setOpenDialog(true);
  };

  const handleEditClick = (id) => {
    setEditingId(id);
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setEditingId(null);
  };

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTargetId, setConfirmTargetId] = useState(null);
  const [confirmName, setConfirmName] = useState('');
  const [confirmLoading, setConfirmLoading] = useState(false);

  const handleDelete = (id, itemName) => {
    setConfirmTargetId(id);
    setConfirmName(itemName || '');
    setConfirmOpen(true);
  };

  const handleConfirmClose = () => {
    if (confirmLoading) return;
    setConfirmOpen(false);
    setConfirmTargetId(null);
    setConfirmName('');
  };

  const handleConfirmDelete = async () => {
    if (!confirmTargetId) return;
    setConfirmLoading(true);
    try {
      await deleteVtds(confirmTargetId);
      notify({ message: t('masterVtds.deleteSuccess', 'GPS successfully deleted'), severity: 'success' });
      setPaginationModel((p) => ({ ...p }));
    } catch (err) {
      console.error(err);
      notify({ message: t('masterVtds.deleteFailed', 'Failed to delete GPS'), severity: 'error' });
    } finally {
      setConfirmLoading(false);
      setConfirmOpen(false);
      setConfirmTargetId(null);
      setConfirmName('');
    }
  };

  const columns = [
    {
      field: 'company',
      headerName: t('masterCompany.company', 'Company'),
      flex: 1.5,
      valueGetter: (value, row) => row?.join?.company_name
    },
    {
      field: 'license_plate',
      headerName: t('masterRole.vehicle', 'Vehicle'),
      flex: 1.2,
      valueGetter: (value, row) => row?.join?.vehicle_license_plate
    },
    { field: 'no_gsm', headerName: t('masterVtds.simcardNo', 'SIM Card Number'), flex: 1.5 },
    { field: 'gps_type', headerName: t('masterVtds.gpsType', 'GPS Type'), flex: 1 },
    { field: 'status_gps', headerName: t('masterVtds.gpsStatus', 'GPS Status'), hide: true },
    { field: 'imei_no', headerName: t('masterVtds.imeiNo', 'IMEI Number'), flex: 1.5 },
    { field: 'gps_vendor', headerName: t('masterVtds.gpsVendor', 'GPS Vendor'), flex: 1.2 },
    { field: 'port', headerName: t('masterVtds.port', 'Port'), flex: 0.8 },
    { field: 'blacklist', headerName: t('masterVtds.blacklist', 'Blacklist'), hide: true },
    { field: 'description', headerName: t('general.description', 'Description'), flex: 2 },
    { field: 'din1_desc', headerName: t('masterVtds.din1', 'Digital Input Number 1'), hide: true },
    { field: 'din2_desc', headerName: t('masterVtds.din2', 'Digital Input Number 2'), hide: true },
    { field: 'din3_desc', headerName: t('masterVtds.din3', 'Digital Input Number 3'), hide: true },
    { field: 'din4_desc', headerName: t('masterVtds.din4', 'Digital Input Number 4'), hide: true },
    { field: 'din5_desc', headerName: t('masterVtds.din5', 'Digital Input Number 5'), hide: true },
    { field: 'fuel_desc', headerName: t('masterVtds.fuel', 'Fuel'), hide: true },
    { field: 'have_ain', headerName: t('masterVtds.haveAin', 'Have AIN'), hide: true },
    { field: 'ain', headerName: t('masterVtds.ainValue', 'AIN Value'), hide: true },
    { field: 'eco_drive_supported', headerName: t('masterVtds.ecoDriveSupported', 'Eco Drive Supported'), hide: true },
    { field: 'buzzer_installed', headerName: t('masterVtds.buzzerInstalled', 'Buzzer Installed'), hide: true },
    { field: 'is_ev', headerName: 'is_ev', hide: true, valueGetter: (value, row) => ((row?.row ?? row)?.is_ev ? 'True' : 'False') },
    { field: 'dist_in', headerName: 'dist_in', hide: true },
    { field: 'stick_fuel_sensor', headerName: t('masterVtds.stickFuelSensor', 'Stick Fuel Sensor'), hide: true },
    { field: 'canbus_fuel_sensor', headerName: t('masterVtds.canbusFuelSensor', 'Canbus Fuel Sensor'), hide: true },
    { field: 'integration_vendor', headerName: t('masterVtds.integrationVendor', 'Integration Vendor'), hide: true },
    { field: 'integration_imei', headerName: t('masterVtds.integrationImei', 'Integration IMEI'), hide: true },
    { field: 'maximum_allow_data', headerName: t('masterVtds.maximumAllowData', 'Maximum Allow Data'), hide: true },
    { field: 'purchase_from', headerName: t('masterVtds.purchaseFrom', 'Purchase From'), hide: true },
    { field: 'purchase_date', headerName: t('masterVtds.purchaseDate', 'Purchase Date'), flex: 1.2 },
    { field: 'selling_price', headerName: t('masterVtds.sellingPrice', 'Selling Price'), hide: true },
    { field: 'selling_date', headerName: t('masterVtds.sellingDate', 'Selling Date'), hide: true },
    { field: 'purchase_price', headerName: t('masterVtds.purchasePrice', 'Purchase Price'), hide: true },
    { field: 'waranty_date', headerName: t('masterVtds.warrantyDate', 'Warranty Date'), hide: true },
    { field: 'insurance_no', headerName: t('masterVtds.insuranceNo', 'Insurance Number'), hide: true },
    { field: 'insurance_date', headerName: t('masterVtds.insuranceDate', 'Insurance Date'), hide: true },

    ...(currentUserRole === 'Administrator'
      ? [
          { field: 'created_by', headerName: t('masterCompany.createdBy', 'Created By'), flex: 0.5, hide: true },
          { field: 'created_date', headerName: t('masterCompany.createdDate', 'Created Date'), flex: 0.5, hide: true },
          { field: 'modified_by', headerName: t('masterCompany.modifiedBy', 'Modified By'), flex: 0.5, hide: true },
          { field: 'modified_date', headerName: t('masterCompany.modifiedDate', 'Modified Date'), flex: 0.5, hide: true }
        ]
      : []),
    {
      field: 'actions',
      headerName: t('general.actions', 'Actions'),
      sortable: false,
      width: 120,
      headerAlign: 'center',
      renderCell: (params) => (
        <ActionMenu
          row={params.row}
          onEdit={handleEditClick}
          onDelete={(id) => handleDelete(id, params.row?.join?.vehicle_license_plate || params.row?.imei_no)}
          mode="inline"
        />
      )
    }
  ];

  return (
    <div>
      <DataTable
        title={t('masterVtds.masterGps', 'Master GPS')}
        rows={rows || []}
        columns={columns}
        loading={isLoading}
        pageSizeOptions={[10, 20, 50, 100]}
        paginationMode="server"
        rowCount={totalRecord}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePageChange}
        searchQuery={searchRaw}
        onSearch={({ field, value }) => {
          setSearchField(field);
          setSearchRaw(value ?? '');
          setPaginationModel((p) => ({ ...p, page: 0 }));
        }}
        searchFieldOptions={searchFieldOptions}
        selectedSearchField={searchField}
        onSearchFieldChange={(f) => {
          setSearchField(f);
          setSearchRaw('');
          setSearchValue('');
          setPaginationModel((p) => ({ ...p, page: 0 }));
        }}
        onAddClick={handleAddClick}
        searchLabel={t('masterVtds.searchGps', 'Search GPS...')}
        companyOptions={companyOptionsNormalized}
        selectedCompanies={selectedCompanies}
        onCompanyChange={handleCompanyChange}
        onFetchAllForExport={handleFetchAllForExport}
      />

      {openDialog && (
        <MasterVtdsAddEdit
          open={openDialog}
          id={editingId}
          companyOptions={companyOptionsNormalized}
          onClose={handleDialogClose}
          onSuccess={() => setPaginationModel((p) => ({ ...p }))}
        />
      )}

      <DeleteConfirmDialog
        open={confirmOpen}
        loading={confirmLoading}
        onClose={handleConfirmClose}
        onConfirm={handleConfirmDelete}
        title={t('general.confirmDelete', 'Confirm Delete')}
        message="Please type the vehicle license plate or IMEI below to confirm the deletion of this GPS data."
        confirmLabel="Continue Delete"
        cancelLabel={t('general.cancel', 'Cancel')}
        itemName={confirmName}
        requireInputMatch={true}
        matchText={confirmName}
      />
    </div>
  );
}

export default function MasterVtdsPage() {
  return <MasterVtdsPageContent />;
}
